<script setup>

const route = useRoute();

const props = defineProps({
    totalPages: {
        type: Number,
        required: true,
    },
    currentPage: {
        type: Number,
        required: true,
    },
    limit: {
        type: Number,
        required: true,
    },
    content: {
        type: String,
        required: true,
    },
});

const pageHref = (page) => {
    const query = { ...route.query, page };
    if (page === 1) delete query.page;
    const qs = new URLSearchParams(query).toString();
    return qs ? `${route.path}?${qs}` : route.path;
};

const truncatedPages = ref([]);

// if there are filter params in the api call
watch(() => props.totalPages, (newValue, oldValue) => {
    truncatedPages.value = paginate(props.currentPage, props.totalPages);
});

watch(() => props.currentPage, (newValue, oldValue) => {
    truncatedPages.value = paginate(props.currentPage, props.totalPages);
});

const paginate = (currentPage, totalPages) => {
    if (!totalPages) return false; // No pages to paginate

    const lastPage = totalPages;
    const pageRange = Array.from({ length: totalPages }, (v, k) => k + 1);

    if (lastPage <= 2) return false; // Not enough pages to paginate

    if (lastPage < 7) {
        // Less than 7 pages, so just show all pages
        pageRange.shift(); // Remove the first page (1)
        pageRange.pop(); // Remove the last page (lastPage)
        return pageRange;
    }

    const isStart = currentPage <= 4;
    const isEnd = currentPage >= lastPage - 4;

    // Calculate the start and end slices for the visible pages
    const startSlice = isStart ? 1 : isEnd ? lastPage - 6 : currentPage - 3;
    const endSlice = isEnd ? lastPage - 1 : isStart ? 6 : currentPage + 2;
    const pagesToShow = pageRange.slice(startSlice, endSlice);

    // Add placeholders (0) for ellipsis if not at the start or end
    if (!isStart) pagesToShow.unshift(0);
    if (!isEnd) pagesToShow.push(0);

    return pagesToShow;
};

const emit = defineEmits(['changePage']);

const changePage = (num, type) => {
    let current = props.currentPage;
    if (type === 'next') {
        if (current < props.totalPages) {
            current += 1;
        }
    } else if (type === 'previous') {
        if (current > 1) {
            current -= 1;
        }
    } else {
        if (current !== num) {
            current = num;
        }
    }
    emit('changePage', current);
};

defineExpose({
    changePage,
});

onMounted(() => {
    if (props.totalPages > 0) {
        truncatedPages.value = paginate(props.currentPage, props.totalPages);
    }
});
</script>

<template>
    <nav class="mess-pagination flex justify-center items-center" aria-label="Pagination">
        <MessLink :href="pageHref(currentPage - 1)" class="arrow previous flex items-center border-2 border-transparent m-0 no-underline hover:border-blue-500 focus:border-2 focus:border-dotted focus:border-blue-500"
            @click.prevent="changePage(null, 'previous')"
            :aria-disabled="currentPage === 1"
            :class="{ 'cursor-not-allowed pointer-events-none': currentPage === 1 }"
            :tabindex="currentPage === 1 ? -1 : 0">
            <span class="sr-only">go to Previous {{ content }}</span>
            <slot name="left-arrow" />
        </MessLink>
        <ul class="flex items-center gap-[1rem] mx-[1rem]">
            <li>
                <MessLink :href="pageHref(1)" :class="{ active: currentPage === 1 }" class="number flex items-center border-2 border-transparent m-0 no-underline hover:border-blue-500 focus:border-2 focus:border-dotted focus:border-blue-500"
                    :aria-current="currentPage === 1 ? 'page' : undefined" @click.prevent="changePage(1, null)">
                    <span class="sr-only" v-if="currentPage !== 1">Go to page </span>
                    1
                </MessLink>
            </li>
            <li v-for="(num, index) in truncatedPages" :key="`trunc-page-${index}`">
                <span v-if="num === 0" class="number">...</span>

                <MessLink v-else :href="pageHref(num)" :class="{ active: num === currentPage }" class="number flex items-center border-2 border-transparent m-0 no-underline hover:border-blue-500 focus:border-2 focus:border-dotted focus:border-blue-500"
                    :aria-current="num === currentPage ? 'page' : undefined" @click.prevent="changePage(num, null)">
                    <span class="sr-only" v-if="num !== currentPage">Go to page </span>
                    {{ num }}
                </MessLink>
            </li>
            <li>
                <MessLink :href="pageHref(totalPages)" :class="{ active: totalPages === currentPage }" class="number flex items-center border-2 border-transparent m-0 no-underline hover:border-blue-500 focus:border-2 focus:border-dotted focus:border-blue-500"
                    :aria-current="totalPages === currentPage ? 'page' : undefined" @click.prevent="changePage(totalPages, null)">
                    <span class="sr-only" v-if="totalPages !== currentPage">Go to page </span>
                    {{ totalPages }}
                </MessLink>
            </li>
        </ul>
        <MessLink :href="pageHref(currentPage + 1)" class="arrow next flex items-center border-2 border-transparent m-0 no-underline hover:border-blue-500 focus:border-2 focus:border-dotted focus:border-blue-500"
            @click.prevent="changePage(null, 'next')"
            :class="{ 'cursor-not-allowed pointer-events-none': currentPage === totalPages }"
            :aria-disabled="currentPage === totalPages"
            :tabindex="currentPage === totalPages ? -1 : 0">
            <slot name="right-arrow" />
            <span class="sr-only">go to Next {{ content }}</span>
        </MessLink>
    </nav>
</template>

